import {
  batch,
  createContext,
  createMemo,
  createRenderEffect,
  createSignal,
  JSX,
  on,
  useContext,
  type Accessor,
} from 'solid-js';

import type { FxWorkspaceContextType } from './fx-workspace-context.types';
import type { MissionPathRef } from './map-mission-path-reference';
import type { FxWorkspaceRepository } from './fx-workspace-repository';
import type { FxWorkspaceLoadStatus } from './fx-workspace.types';
import { combineWorkspaceDetailsLoadStatus } from './fx-workspace-query';
import { useFxExtractionWorkflow } from './use-fx-extraction-workflow';
import { useFxWorkspaceBreaklineActions } from './use-fx-workspace-breakline-actions';
import { useFxWorkspaceBreaklines } from './use-fx-workspace-breaklines';
import { useFxWorkspaceDraftLayers } from './use-fx-workspace-draft-layers';
import { useFxWorkspaceDataEditing } from './use-fx-workspace-data-editing';
import { useFxWorkspaceList } from './use-fx-workspace-list';
import { useFxWorkspaceReferences } from './use-fx-workspace-references';

const FxWorkspaceContext = createContext<FxWorkspaceContextType>();

export function FxWorkspaceContextProvider(props: {
  children: JSX.Element;
  repository: Accessor<FxWorkspaceRepository>;
  repositoryKey: Accessor<string>;
  getMissionPathRefs?: Accessor<readonly MissionPathRef[]>;
}): JSX.Element {
  const list = useFxWorkspaceList(props);

  const detailsKey = createMemo(() => {
    const key = list.fetchKey();
    const workspaceId = list.activeWorkspaceId();
    if (
      key === undefined ||
      workspaceId === undefined ||
      list.loadStatus() !== 'success'
    ) {
      return undefined;
    }

    return { key, workspaceId };
  });

  const references = useFxWorkspaceReferences({
    repository: list.repository,
    fetchKey: list.fetchKey,
    workspaces: list.workspaces,
    activeWorkspaceId: list.activeWorkspaceId,
    detailsKey,
  });

  const breaklines = useFxWorkspaceBreaklines({
    repository: list.repository,
    fetchKey: list.fetchKey,
    workspaces: list.workspaces,
    activeWorkspaceId: list.activeWorkspaceId,
    detailsKey,
  });

  const breaklineActions = useFxWorkspaceBreaklineActions({
    repository: list.repository,
    getBreakline: breaklines.getBreakline,
    appendWorkspaceBreakline: breaklines.appendWorkspaceBreakline,
    reloadWorkspaceBreaklines: breaklines.reloadWorkspaceBreaklines,
  });

  const draftLayers = useFxWorkspaceDraftLayers({
    listWorkspaceBreaklines: breaklines.listWorkspaceBreaklines,
  });

  const extraction = useFxExtractionWorkflow();
  const missionPathRefs = createMemo(
    (): readonly MissionPathRef[] => props.getMissionPathRefs?.() ?? []
  );
  const editing = useFxWorkspaceDataEditing({
    activeWorkspaceId: list.activeWorkspaceId,
    getWorkspaceReferences: references.getWorkspaceReferences,
    areWorkspaceReferencesLoaded: references.areWorkspaceReferencesLoaded,
    syncWorkspaceReferences: references.syncWorkspaceReferences,
    getMissionPathRefs: missionPathRefs,
  });

  const [extractedFeatureNodeVisibility, setExtractedFeatureNodeVisibility] =
    createSignal<Record<string, boolean>>({});

  const isExtractedFeatureNodeVisible = (nodeId: string): boolean =>
    extractedFeatureNodeVisibility()[nodeId] ?? true;

  const toggleExtractedFeatureNodeVisibility = (nodeId: string): void => {
    setExtractedFeatureNodeVisibility((previous) => ({
      ...previous,
      [nodeId]: !(previous[nodeId] ?? true),
    }));
  };

  const activeWorkspaceDetailsLoadStatus = createMemo(
    (): FxWorkspaceLoadStatus =>
      combineWorkspaceDetailsLoadStatus(
        detailsKey(),
        references.loadStatus(),
        breaklines.loadStatus()
      )
  );

  const deleteWorkspace = async (workspaceId: string): Promise<boolean> => {
    const deleted = await list.deleteWorkspace(workspaceId);
    if (!deleted) {
      return false;
    }

    references.clearWorkspace(workspaceId);
    breaklines.clearWorkspace(workspaceId);
    breaklineActions.clearWorkspace(workspaceId);
    draftLayers.clearWorkspace(workspaceId);
    return true;
  };

  // Reset FX-scoped signals in place. A keyed remount would also destroy
  // props.children, and this provider wraps the router and 3D viewer.
  // Each store invalidates in-flight loads and mutations so they cannot
  // write into the new repository scope or cache under the new fetchKey.
  createRenderEffect(
    on(
      () => props.repositoryKey(),
      () => {
        batch(() => {
          list.resetRepositoryScope();
          references.resetRepositoryScope();
          editing.resetSession();
          breaklines.resetRepositoryScope();
          breaklineActions.resetRepositoryScope();
          draftLayers.reset();
          extraction.resetRepositoryScope();
          setExtractedFeatureNodeVisibility({});
        });
      },
      { defer: true }
    )
  );

  const value: FxWorkspaceContextType = {
    isEnabled: list.isEnabled,
    loadStatus: list.loadStatus,
    workspaces: list.workspaces,
    selectedWorkspaceId: list.selectedWorkspaceId,
    selectedJobId: list.selectedJobId,
    activeWorkspaceId: list.activeWorkspaceId,
    activeWorkspaceDetailsLoadStatus,
    setSelectedWorkspaceId: list.setSelectedWorkspaceId,
    setSelectedJobId: list.setSelectedJobId,
    activateWorkspace: list.activateWorkspace,
    deactivateWorkspace: list.deactivateWorkspace,
    isWorkspaceActive: list.isWorkspaceActive,
    createWorkspace: list.createWorkspace,
    deleteWorkspace,
    loadWorkspaceReferences: references.loadWorkspaceReferences,
    isReferenceVisible: references.isReferenceVisible,
    areWorkspaceReferencesLoaded: references.areWorkspaceReferencesLoaded,
    workspaceReferenceCount: references.workspaceReferenceCount,
    toggleReferenceVisibility: references.toggleReferenceVisibility,
    addReference: references.addReference,
    removeReference: references.removeReference,
    isWorkspaceDataEditing: editing.isWorkspaceDataEditing,
    editingWorkspaceId: editing.editingWorkspaceId,
    isEditingWorkspaceReferencesLoaded:
      editing.isEditingWorkspaceReferencesLoaded,
    draftReferences: editing.draftReferences,
    enterWorkspaceDataEditing: editing.enterWorkspaceDataEditing,
    toggleDraftReference: editing.toggleDraftReference,
    isDraftReference: editing.isDraftReference,
    setDraftReferenceSelection: editing.setDraftReferenceSelection,
    commitWorkspaceDataEditing: editing.commitWorkspaceDataEditing,
    cancelWorkspaceDataEditing: editing.cancelWorkspaceDataEditing,
    isExtractedFeatureNodeVisible,
    toggleExtractedFeatureNodeVisibility,
    loadWorkspaceBreaklines: breaklines.loadWorkspaceBreaklines,
    listWorkspaceBreaklines: breaklines.listWorkspaceBreaklines,
    getBreakline: breaklines.getBreakline,
    isBreaklineCreating: breaklineActions.isBreaklineCreating,
    isBreaklinesBuilding: breaklineActions.isBreaklinesBuilding,
    createBreakline: breaklineActions.createBreakline,
    runBreaklinesExtraction: breaklineActions.runBreaklinesExtraction,
    publishBreakline: breaklineActions.publishBreakline,
    publishAllBreaklines: breaklineActions.publishAllBreaklines,
    listDraftLayers: draftLayers.listDraftLayers,
    createDraftLayer: draftLayers.createDraftLayer,
    selectedExtractionWorkflow: extraction.selectedExtractionWorkflow,
    isNewExtractionDialogOpen: extraction.isNewExtractionDialogOpen,
    openNewExtractionDialog: extraction.openNewExtractionDialog,
    closeNewExtractionDialog: extraction.closeNewExtractionDialog,
    requestChangeExtractionWorkflow: extraction.requestChangeExtractionWorkflow,
    confirmExtractionWorkflowSelection:
      extraction.confirmExtractionWorkflowSelection,
  };

  return (
    <FxWorkspaceContext.Provider value={value}>
      {props.children}
    </FxWorkspaceContext.Provider>
  );
}

export function useFxWorkspaceContext(): FxWorkspaceContextType {
  const context = useContext(FxWorkspaceContext);
  if (!context) {
    throw new Error(
      'useFxWorkspaceContext requires FxWorkspaceContextProvider'
    );
  }
  return context;
}
